import React from 'react';
import { Link } from 'react-router-dom';

const AdminGuide: React.FC = () => {
  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">Admin guide</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              A quick walkthrough of the main TELLURVISION admin tools so new administrators can
              get oriented in a few minutes.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
            <Link
              to="/admin"
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Admin overview
            </Link>
          </div>
        </header>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Dashboard walkthrough</h2>
          <p>
            The Admin dashboard tiles are meant to point you to the right tool quickly. Use this
            guide when you want the exact step-by-step workflow behind each section.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Manage users</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs sm:text-sm">
                <li>Open User management from the dashboard.</li>
                <li>Find the person by name or email in the user table.</li>
                <li>Use View to open the full account profile.</li>
                <li>Review profile details, assigned ads, and payment history.</li>
                <li>Return to the table if you need to remove or manage another account.</li>
              </ol>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ads management</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs sm:text-sm">
                <li>Open Ads management from the dashboard.</li>
                <li>Filter the creative library by title or owner if needed.</li>
                <li>Select one or more ads from the list.</li>
                <li>Choose the target user in the bulk-assign panel.</li>
                <li>Open any ad detail page when you need deeper analytics or ownership review.</li>
              </ol>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payments</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs sm:text-sm">
                <li>Open the Payments page to review transaction records.</li>
                <li>Check status, amount, package, and linked ad information.</li>
                <li>Open a user profile when you need one customer&apos;s payment footprint.</li>
                <li>Confirm whether campaign funding is completed before troubleshooting access.</li>
              </ol>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Campaign packages</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs sm:text-sm">
                <li>Open Campaign packages from the dashboard.</li>
                <li>Review the existing package names, prices, and ranges.</li>
                <li>Edit an existing package or create a new one.</li>
                <li>Save the package and verify the payment flow still shows the right options.</li>
              </ol>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">1. Accounts and access</h2>
          <p>
            Use <span className="font-medium">Admin → User management</span> to create advertiser
            accounts, promote team members, and keep the network healthy.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Create advertisers:</span> click
              <span className="font-medium"> Add user</span>, set role to
              <span className="font-medium"> user</span>, and keep account status set to
              <span className="font-medium"> good</span>.
            </li>
            <li>
              <span className="font-medium">Pause access:</span> switch account status to
              <span className="font-medium"> suspended</span> or
              <span className="font-medium"> review</span> to temporarily block logins.
            </li>
            <li>
              <span className="font-medium">View details:</span> click any user row to see their
              recent activity and basic profile.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">2. Creatives (ads)</h2>
          <p>
            Creatives are the videos or images that play on the billboard. From
            <span className="font-medium"> Admin → Ads</span> you can review what&apos;s live and
            who owns it.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Review inventory:</span> browse ads, check titles and
              owners, and remove anything that should not run.
            </li>
            <li>
              <span className="font-medium">Link to campaigns:</span> when an advertiser funds a
              campaign with a creative selected, the payment record stores the creative ID so you
              can audit &ldquo;who paid for what.&rdquo;
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">3. Campaign packages</h2>
          <p>
            Campaign packages are the presets advertisers see during checkout. Configure them under
            <span className="font-medium"> Admin → Campaign packages</span>.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Name &amp; describe:</span> write copy that explains
              where and how the package runs (for example, &ldquo;City loop spotlight&rdquo;).
            </li>
            <li>
              <span className="font-medium">Base price:</span> the typical or &ldquo;from&rdquo; price you
              want to highlight in marketing.
            </li>
            <li>
              <span className="font-medium">Min / max budget:</span> the spend range where this
              package should be suggested on the Payments page. Amounts inside the range will flag
              this package as a good fit.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">4. Start a campaign flow</h2>
          <p>
            Advertisers follow a three‑step wizard that you can walk them through:
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Choose a package:</span> pick from the presets you
              configured.
            </li>
            <li>
              <span className="font-medium">Choose a creative (optional):</span> select one of
              their ads, or continue without a creative if they are just pre‑funding budget.
            </li>
            <li>
              <span className="font-medium">Review &amp; fund:</span> confirm details, then move to
              the Payments page where they enter card details.
            </li>
          </ol>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">5. Payments and reporting</h2>
          <p>
            Payments are visible to both advertisers and admins and act as the system of record
            for funded campaigns.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Advertiser view:</span> the
              <span className="font-medium"> Payments</span> page shows their own recent payments,
              confirmation details, and a summary of suggested packages.
            </li>
            <li>
              <span className="font-medium">Admin view:</span> the
              <span className="font-medium"> Payments by campaign</span> section in the Admin
              panel lets you filter payments by user, package, or creative.
            </li>
            <li>
              <span className="font-medium">Reference IDs:</span> each payment stores a unique ID
              and timestamp so finance and support can match inquiries to specific transactions.
            </li>
            <li>
              <span className="font-medium">Campaign payments:</span> when an advertiser completes
              the <span className="font-medium">Start a campaign</span> wizard and then pays on the
              <span className="font-medium"> Payments</span> page, the system records a payment
              that is linked to a specific campaign package (and optionally a creative). Those
              payments power the advertiser&apos;s <span className="font-medium">My campaigns</span>
              view and your <span className="font-medium">Payments by campaign</span> analytics.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">6. Billboard operations</h2>
          <p>
            Use the widgets on the main Admin panel to keep the network healthy.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Billboard control panel:</span> simulate safe/unsafe
              states in test environments and verify that the player responds correctly.
            </li>
            <li>
              <span className="font-medium">Health card:</span> confirms when the billboard agent
              is online and sending telemetry.
            </li>
            <li>
              <span className="font-medium">Analytics:</span> review daily impressions and share
              activity to understand which creatives are getting traction.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">7. Camera plugins & safety telemetry</h2>
          <p>
            TELLURVISION&apos;s crash detection, gaze awareness, and object recognition all run on
            the <span className="font-medium">device</span> and report back only high‑level
            telemetry. The admin tools never see raw video or face images – just metrics and
            alerts.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">On‑device plugins:</span> camera plugins compute
              things like <span className="font-medium">driver attention</span>,
              <span className="font-medium">impact events</span>, or
              <span className="font-medium">people detected ahead</span>. They emit compact JSON
              events to the backend using the devices API (for example,
              <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[11px]">POST
              /api/devices/&lt;deviceId&gt;/events</code>).
            </li>
            <li>
              <span className="font-medium">Backend rollups:</span> the backend stores these
              events and exposes a lightweight per‑device snapshot via
              <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[11px]">GET
              /api/devices/&lt;deviceId&gt;/telemetry</code>. The Admin billboard test player and
              health widgets consume this snapshot to show speed, last event time, and people
              counts.
            </li>
            <li>
              <span className="font-medium">Safety decisions:</span> when the billboard player is
              showing a real ad, it periodically calls the safety analysis endpoint for that ad
              (for example,
              <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[11px]">POST
              /api/ads/&lt;adId&gt;/telemetry/analyze</code>). The response includes a
              <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[11px]">shouldBlankAd</code>
              flag that tells the player when to blank the screen instead of showing creative.
            </li>
            <li>
              <span className="font-medium">How this shows up in Admin:</span> the
              <span className="font-medium">Billboard health</span> card, control panel, and test
              player on the Admin page are wired to these telemetry APIs. When devices (or the
              emulator) send updated events, admins immediately see changes in speed, blanking
              behavior, and simple safety indicators.
            </li>
            <li>
              <span className="font-medium">Local testing:</span> in development you can use the
              provided device emulators or scripts to post events to
              <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[11px]">/api/devices/&lt;deviceId&gt;/events</code>,
              then open the Admin panel&apos;s billboard section to verify that telemetry and
              blanking respond as expected.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">8. Adding new billboards</h2>
          <p>
            To put TELLURVISION on another physical billboard, you do not copy the whole app –
            you point a fullscreen browser at the billboard player URL with a unique screen ID.
            In most modern signs this runs directly on the billboard&apos;s built‑in controller; only
            when a sign doesn&apos;t support a browser do you fall back to a small external player
            device.
          </p>
          <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 mt-1">
            Using the billboard&apos;s built‑in controller (preferred)
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Assign a screen ID:</span> choose an ID that encodes
              location and direction (for example <code>BOS-I93-NORTH-001</code>) and record it in
              your inventory.
            </li>
            <li>
              <span className="font-medium">Log into the controller:</span> use the billboard
              vendor&apos;s instructions to sign in to the built‑in controller or &ldquo;player&rdquo;
              web interface (often a local IP or vendor portal).
            </li>
            <li>
              <span className="font-medium">Find the startup URL / web app setting:</span> look
              for a field called something like <span className="font-medium">Start URL</span>,
              <span className="font-medium">Web app URL</span>, or
              <span className="font-medium">Content URL</span> and enter the billboard URL with
              your screen ID, for example
              <code>https://app.your-domain.com/billboard?screenId=BOS-I93-NORTH-001</code>.
            </li>
            <li>
              <span className="font-medium">Enable fullscreen / kiosk mode:</span> turn on any
              options like <span className="font-medium">Run in kiosk mode</span>,
              <span className="font-medium">Hide browser chrome</span>, or
              <span className="font-medium">Auto‑launch on boot</span> so the browser opens
              fullscreen to that URL whenever the controller powers up.
            </li>
            <li>
              <span className="font-medium">Confirm network access:</span> verify the controller
              can reach your backend and app in a browser (for example
              <code>https://api.your-domain.com/health</code> and
              <code>https://app.your-domain.com/</code>). If those do not load, work with your
              network team to allow outbound HTTPS.
            </li>
            <li>
              <span className="font-medium">Verify in Admin:</span> open the Admin dashboard,
              check the billboard health/widgets for that screen ID, and use the test controls to
              confirm that telemetry arrives and the player blanks ads when
              <code>shouldBlankAd</code> is true.
            </li>
          </ol>
          <p className="text-xs text-slate-700 dark:text-slate-300 mt-2">
            If a particular sign does not support a built‑in browser or startup URL, you can fall
            back to a small external player device (mini‑PC / Raspberry Pi) that runs a fullscreen
            browser pointed at the same billboard URL.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            For a more detailed operator checklist and a minimal standalone HTML player example,
            refer to the internal docs under <span className="font-medium">BILLBOARD_PLAYER_SETUP</span>.
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">9. AWS deployment quick reference</h2>
          <p>
            When you are ready to run TELLURVISION on real billboards, the recommended cloud
            setup is <span className="font-medium">AWS Elastic Beanstalk</span> for the backend
            API and <span className="font-medium">S3 + CloudFront</span> for the web app.
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-xs sm:text-sm">
            <li>
              <span className="font-medium">Create MongoDB Atlas:</span> provision a small
              cluster, create a database user, and copy the connection string
              (<code>mongodb+srv://…/TELLURVISION</code>).
            </li>
            <li>
              <span className="font-medium">Deploy backend with Elastic Beanstalk:</span> zip
              the <span className="font-medium">TELLURVISION-website</span> app, create a
              Node.js environment, set env vars (including <code>MONGO_URI</code> and
              <code>ALLOWED_ORIGINS=https://app.your-domain.com</code>), and confirm that the
              <code>/health</code> endpoint is green.
            </li>
            <li>
              <span className="font-medium">Deploy frontend to S3 + CloudFront:</span> build
              <span className="font-medium">telluvision-web-app</span>, upload the
              <code>build/</code> contents to S3, and put CloudFront in front with
              <code>index.html</code> as the default.
            </li>
            <li>
              <span className="font-medium">Attach domains and HTTPS:</span> use ACM + Route 53
              to point <code>api.your-domain.com</code> at Elastic Beanstalk and
              <code>app.your-domain.com</code> at CloudFront.
            </li>
            <li>
              <span className="font-medium">Run a live smoke test:</span> open
              <code>https://app.your-domain.com</code>, walk through admin + advertiser flows,
              and then point a single billboard controller at
              <code>/billboard?screenId=YOUR-TEST-SCREEN</code> to verify telemetry and safety
              blanking.
            </li>
          </ol>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            For a detailed, step‑by‑step deployment guide (including rollback and staging
            workflow), see the <span className="font-medium">PRODUCTION_RUNBOOK</span> in the
            internal docs.
          </p>
        </section>

        <section className="flex flex-wrap gap-2 text-xs sm:text-sm">
          <Link
            to="/admin/user-management"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go to User management
          </Link>
        </section>
      </div>
    </main>
  );
};

export default AdminGuide;
